/* eslint-disable no-console */
// TODO: Remove console logs after intital dev and testing

import { groupBy, indexBy } from 'underscore'
import { type CartLineItem } from '@/services/cart/cart'
import { flattenLineItemOps, type LineItemOpRequest } from '@/api/cart-adapter'
import { createUrlSafeUuidV4 } from '@/utils/uuidUtils'
import { itemCartContextData } from '@/services/item'
import { ItemCartContext } from '@/services/cart/types'
import { LineItemOpDestroyProofAction } from '@/api/mercury/cart'

/**
 * Represents the minimal information from backend cart line item state, that's needed
 * to initialize a client-side cart to expand line item ops for Avetti.
 * This enables creating an abstraction to use new features with the cart service, even with the legacy Avetti backend.
 */
export type LightCartLineItem = Pick<
  CartLineItem,
  | 'uuid'
  | 'parentUuid'
  | 'customizationId'
  | 'itemId'
  | 'quantity'
  | 'quantityRecipients'
  // TODO: Add passthrough support for scheduled ship date for Mercury.
  //   For Avetti, we can have an independent function to validate that only 1 line item op has this set
  //   (until we extract recipients), then call `setScheduledShipDate`.
  // | 'scheduledShipDate'
> & {
  // TODO: Move to CartLineItem and set from the adapter: deriving for Avetti, mapping from the backend for Mercury
  // TODO: Set quantitySyncMultiplier using attribute from the backend proof API when adding extra pages for photo books
  // May be set on a child to multiply sync behavior.
  // Mercury will support this in the cart, but it can be faked with Avetti from the caller:
  // if an item has `quantitySyncBehavior=MAIN_MULTIPLIED_BY_FULFILLMENT_ATTRIBUTE`, and its parent has
  // `quantitySyncFulfillmentAttribute` defined, and the parent's proof's corresponding fulfillment attribute
  // is set, use that number. For `MAIN_MULTIPLIED_BY_FULFILLMENT_ATTRIBUTE` where a valid value can't be located,
  // use 0 to make the child disappear, and log a warning since that scenario shouldn't be possible.
  // Effectively defaults to 1 if not defined.
  quantitySyncMultiplier: number | null
}

/**
 * Holds the state of "virtual" cart items in memory, used to track and consolidate operations
 * These are created from an existing cart (and saved for later) and related proof and item data.
 * Line item operations then add or mutate these, along with related behaviors like quantity sync
 * or recursive children-based operations like moving to and from saved for later, or destroying.
 */
export type MutableCartLineItem = LightCartLineItem & {
  savedForLater: boolean
  _new: boolean
  item: ItemCartContext
}

type LineItemOpWithItem = LineItemOpRequest & {
  item: ItemCartContext | null
  children?: LineItemOpWithItem[]
}

/**
 * Line items can be identified in the cart by the combination of item ID and parent UUID.
 * Returns a string key based on both, for internal lookups and tracking.
 */
const getItemIdParentUuidKey = (lineItemOrOp: {
  itemId: number
  parentUuid?: string | null
}) =>
  // Not guaranteed to be unique within the cart, but we assume it won't be used it if it's not unique.
  // If it is used, one line item will be matched, but which particular line item it will match is undefined.
  // The Mercury backend would detect this and throw an error, but the Avetti behavior would be undefined.
  [lineItemOrOp.itemId, lineItemOrOp.parentUuid ?? null].join()

/**
 * Throws an error if a `lineItemOp` isn't valid.
 * Complements additional checks that will indirectly validate ops when translating them to a line item.
 * Does not necessarily check all scenarios, but prevents definite errors from running undetected.
 * This is mainly to get early feedback to help find dev errors, as the backend should reject bad data anyway.
 */
const validateOp = (lineItemOp: LineItemOpRequest) => {
  // TODO: Remove once correct usage is ensured
  // Guess that an op may be an insert or upsert if itemId is set.
  // This may generate false positives, but this is just a temporary aide for finding uncaught instances.
  if (lineItemOp.uuid && !lineItemOp.customizationId && lineItemOp.itemId) {
    // Temporary console.warn to find proofId usages
    // eslint-disable-next-line no-console
    console.warn(
      'customizationId expected to be set with uuid unless op is an update',
      lineItemOp
    )
  }
  if (
    lineItemOp._incrementQuantity !== true &&
    typeof lineItemOp.quantity === 'number' &&
    lineItemOp.quantity < 0
  ) {
    throw new Error(
      'Cannot set a negative quantity on a line item: ' + lineItemOp.quantity
    )
  }
  if (
    typeof lineItemOp.quantitySyncMultiplier === 'number' &&
    lineItemOp.quantitySyncMultiplier < 0
  ) {
    throw new Error('quantitySyncMultiplier cannot be negative')
  }
  if (lineItemOp._destroy && lineItemOp.children) {
    throw new Error('Cannot use children with _destroy')
  }
  if (lineItemOp.parentUuid) {
    // Detect ops with a `parentUuid` because *current use cases*,
    // which aren't expected to entail updating children without updating the parent,
    // should nest `children` instead and remove the implicit `parentUuid`.
    // This isn't an illegal op, but this warning will help catch calls that should be refactored in the short term.
    // TODO: Remove warning once initial implementation is stable with minimal API calls
    console.warn('`lineItemOp` with `parentUuid` detected', lineItemOp)
  }
}

/**
 * Ensures that children are colocated with their parent.
 * This is a helper called from a recursive function to sync attributes downward.
 */
const syncSavedForLater = (
  parentLineItem: MutableCartLineItem,
  childrenLineItems: MutableCartLineItem[]
) => {
  childrenLineItems.forEach(
    (child) => (child.savedForLater = parentLineItem.savedForLater)
  )
}

/**
 * Overwrites the quantity of direct children line items, if they are subject to quantity sync rules.
 */
const syncQuantities = (
  parentLineItem: MutableCartLineItem,
  childrenLineItems: MutableCartLineItem[]
) => {
  // TODO: Once addresses are extracted from Avetti, the `quantity` for the parent is always `MAIN`.
  //   However, with Avetti managing the recipient list, the quantities passed for line items are always without
  //   recipients because the number of recipients is implicit, and potentially unknown by the caller,
  //   whose intent is to set the non-recipients quantity (e.g., from a dropdown).
  const mainMinusRecipientsQuantity = parentLineItem.quantity
  const recipientsQuantity = parentLineItem.quantityRecipients
  // TODO: Once addresses are extracted from Avetti, the children become the source of truth for `recipientsQuantity`
  //   childrenLineItems
  //     .filter(
  //       (child) => child.quantitySyncGroup === ItemQuantitySyncGroup.Recipients
  //     )
  //     .reduce((sum, child) => sum + child.quantity, 0)
  childrenLineItems.forEach((child) => {
    /**
     * A line item can have an optional attribute called `quantitySyncMultiplier`.
     * If the line item has a quantity synced to it, this multiplier will be used.
     *
     * 1 is the default, so no behavior changes if it's not defined.
     *
     * Example: One use case is having a child line item for extra pages in a photo book.
     * If you add 5 pages to your photo book, you would be charged for 5 extra pages per photo book ordered.
     * In this scenario, the extra pages item would have `quantitySyncBehavior=MAIN` and the extra pages
     * line item would have `quantitySyncMultiplier=5`.
     */
    const syncMultiplier = child.quantitySyncMultiplier ?? 1

    // In the context of quantity sync, we assume there are no conflicting rules like a minimum quantity
    switch (child.item.quantitySyncBehavior) {
      case 'MAIN':
      // MAIN_MULTIPLIED_BY_FULFILLMENT_ATTRIBUTE is replaced with setting an optional syncMultiplier on any line item.
      // Once an item has a quantitySyncMultiplier, MAIN_MULTIPLIED_BY_FULFILLMENT_ATTRIBUTE is the same as MAIN.
      // TODO: After Mercury migration, replace all data with MAIN and remove case
      // eslint-disable-next-line no-fallthrough
      case 'MAIN_MULTIPLIED_BY_FULFILLMENT_ATTRIBUTE':
        child.quantity =
          (mainMinusRecipientsQuantity + recipientsQuantity) * syncMultiplier
        break
      case 'MAIN_MINUS_RECIPIENTS':
        child.quantity = mainMinusRecipientsQuantity * syncMultiplier
        break
      case 'RECIPIENTS':
        child.quantity = recipientsQuantity * syncMultiplier
        break
    }
  })
}

/**
 * Inject item data, mainly to add quantity-related data for new item defaults and quantity sync processing.
 */
const withItemData = async (
  lineItemOps: LineItemOpRequest[]
): Promise<LineItemOpWithItem[]> => {
  // Existing items will already have the necessary data, which should already be cached
  const itemData = await itemCartContextData(
    new Set(
      flattenLineItemOps(lineItemOps)
        .map((lineItemOp) => lineItemOp.itemId)
        .flatMap((id) => (id ? [id] : []))
    )
  )

  /**
   * Add related `item` data to line item ops and their children.
   */
  const withItem = (lineItemOps: LineItemOpRequest[]): LineItemOpWithItem[] =>
    lineItemOps.reduce<LineItemOpWithItem[]>((acc, lineItemOp) => {
      // New items must always have an itemId (validated elsewhere),
      // but an update might only have a `uuid` with other fields to update.
      const item = lineItemOp.itemId ? itemData[lineItemOp.itemId]! : null
      return [
        ...acc,
        {
          ...lineItemOp,
          item,
          children: withItem(lineItemOp.children ?? [])
        }
      ]
    }, [])

  return withItem(lineItemOps)
}

const cartItemToUpsertLineItemOp = (
  cartItem: MutableCartLineItem
): LineItemOpRequest => ({
  itemId: cartItem.itemId,
  quantity: cartItem.quantity,
  uuid: cartItem.uuid,
  parentUuid: cartItem.parentUuid,
  customizationId: cartItem.customizationId,
  savedForLater: cartItem.savedForLater
})

const destroyLineItemOp = ([uuid, destroyProofAction]: [
  string,
  LineItemOpDestroyProofAction
]): LineItemOpRequest => ({
  uuid,
  _destroy: true,
  _destroyProofAction: destroyProofAction
})

/**
 * Partial cart implementation to emulate Mercury-specific cart features implemented in the backend.
 *
 * Porting behavior to the frontend allows the same behavior to run for Avetti, while business logic
 * is simplified to use the new Mercury features.
 *
 * Inserts, updates, and deletions are buffered in memory until all operations are processed to
 * minimize load on the database, allow batch updates, and ensure fast performance.
 *
 * To help with testing and separation of concerns, this class avoids mutating the cart.
 * This receives a subset item data from a helper (which can be mocked), but is constructed with existing cart state,
 * and outputs ops to be committed by the cart service.
 *
 * Notable features:
 * - Fix initial cart state
 *   - Move orphans with their parents (if possible), otherwise destroy orphans
 *   - Saved for later sync to children (recursive)
 * - _destroyExtraChildren = true to destroy existing children that aren't part of a specific line item operation
 * - Recursive quantity sync to direct children, starting from the root line items
 *   - item.quantitySyncBehavior = MAIN
 *   - item.quantitySyncBehavior = MAIN_MINUS_RECIPIENTS
 *   - item.quantitySyncBehavior = RECIPIENTS
 *   - item.quantitySyncGroup = RECIPIENTS
 *   - lineItem.quantitySyncMultiplier
 * - Saved for later sync to children (recursive) from root
 * - Recursive deletion to children via _destroy = true or negative quantity after increment
 */
export class MutableCart {
  /**
   * Unmodified line items, capturing the (assumed to be) existing cart and saved for later state.
   * Commit ops will be generated by simulating changes to this initial state.
   */
  private readonly originalLineItemsByUuid: {
    [uuid: string]: Readonly<MutableCartLineItem>
  }
  /**
   * Mutable line items that already exist in the cart.
   * Line item ops will modify these in memory, to simulate cart behavior,
   * and eventually build commit ops for any changes.
   *
   * Not `readonly` because items are removed via `filter` and reassignment.
   */
  private existingLineItems: MutableCartLineItem[]
  /**
   * Mutable line items that don't already exist in the cart.
   * These will always generate commit item ops since need to be created.
   */
  private readonly newLineItems: MutableCartLineItem[] = []

  /**
   * Line items with duplicate UUIDs that were found while fixing the initial state.
   * Not used for behavior in MutableCart. This is only for external observability.
   */
  readonly duplicates: MutableCartLineItem[] = []
  /**
   * Mutable line item orphans that were found while fixing the initial state.
   * Not used for behavior in MutableCart. This is only for external observability.
   */
  readonly orphans: MutableCartLineItem[] = []
  /**
   * Track line items found by ops to help identify mistakes.
   * Not used for actual cart behavior.
   */
  private readonly processedExistingLineItemUuids: Set<string> = new Set()
  /**
   * Tracks which line items are destroyed, and what their related op's `_destroyProofAction` should be.
   */
  private readonly destroyedLineItemUuidToProofDestroyAction: {
    [uuid: string]: LineItemOpDestroyProofAction
  } = {}
  /**
   * Efficient lookup to find a mutable line item by its `uuid` from either `existingLineItems` or `newLineItems`.
   */
  private readonly currentLineItemsByUuid: {
    [uuid: string]: MutableCartLineItem
  }
  /**
   * Efficient lookup to find a mutable line item by a combination of its item ID and parent UUID (potentially `null`)
   * from either `existingLineItems` or `newLineItems`.
   */
  private readonly currentLineItemsByItemIdParentUuid: {
    [uuid: string]: MutableCartLineItem
  }
  /**
   * Efficient lookup to find children mutable line items by their parent's `uuid`,
   * from either `existingLineItems` or `newLineItems`.
   */
  private readonly currentLineItemChildrenByParentUuid: {
    [uuid: string]: MutableCartLineItem[]
  }
  /**
   * Flag to enforce only getting commit ops once,
   * to avoid accidentally reusing out of date state for the original line items.
   */
  private committed = false

  /**
   * Create a new cart from existing cart and saved for later state.
   * State is expected to be current. If it differs from what the backend has, resulting ops may be wrong.
   *
   * Constructing with initial ops (vs adding them later) improves performance
   * by allowing item data requests for ops to be made in parallel.
   */
  static async create(
    {
      cart,
      savedForLater
    }: {
      cart: LightCartLineItem[]
      savedForLater: LightCartLineItem[]
    },
    initialLineItemOps: LineItemOpRequest[] = []
  ): Promise<MutableCart> {
    const cartPlusSaved = [
      ...cart.map((lineItem) => ({ ...lineItem, savedForLater: false })),
      ...savedForLater.map((lineItem) => ({ ...lineItem, savedForLater: true }))
    ]
    const allInitialItemIds = new Set([
      ...cartPlusSaved.map((lineItem) => lineItem.itemId),
      ...flattenLineItemOps(initialLineItemOps).flatMap((op) =>
        op.itemId ? [op.itemId] : []
      )
    ])

    // Get all data for the initial state and any ops in parallel.
    // Ops will be processed after initial creation, but the cache will already be warmed.
    // TODO: Consider getting all data lazily when generating commit ops so this isn't async,
    //   and especially to batch fetches from additional updates (not just the initial ops).
    //   However, in practice, we'll need to see if that's really even helpful.
    const itemData = await itemCartContextData(allInitialItemIds)

    const mutableCart = new MutableCart(
      cartPlusSaved.map((lineItem): MutableCartLineItem => {
        const item = itemData[lineItem.itemId]
        if (!item) {
          // Should have been fetched above. This should never happen.
          throw new Error(
            `Could not find data for item with id ${lineItem.itemId}`
          )
        }
        const {
          uuid,
          parentUuid,
          customizationId,
          itemId,
          savedForLater,
          quantitySyncMultiplier,
          quantity,
          quantityRecipients
        } = lineItem
        // Explicit mapping avoids passing extra attributes and is enforced by TypeScript due to object literal
        return {
          uuid,
          parentUuid,
          customizationId,
          itemId,
          savedForLater,
          quantitySyncMultiplier,
          // TODO: Update logic and simplify once recipients are extracted from Avetti.
          //   Until then, the `quantity` received in a line item is always the total (recipients + non-recipients)
          //   or MAIN `quantity`. However, this differs from what `quantity` means in MutableCart, which
          //   is MAIN_MINUS_RECIPIENTS, because the recipients quantity is never set directly via cart updates.
          quantity: quantity - quantityRecipients,
          quantityRecipients,
          _new: false,
          item
        }
      })
    )

    if (initialLineItemOps.length > 0) {
      await mutableCart.update(initialLineItemOps)
    }

    return mutableCart
  }

  private constructor(lineItems: MutableCartLineItem[]) {
    // Store each original line item, so we can detect what changed
    this.originalLineItemsByUuid = indexBy(lineItems, 'uuid')
    // Clone to avoid mutating the original line items
    this.existingLineItems = lineItems.map((lineItem) => ({
      // Shallow clone
      ...lineItem
    }))

    // Create efficient lookups for internal use to avoid n^2 lookups
    this.currentLineItemsByUuid = indexBy(this.existingLineItems, 'uuid')
    this.currentLineItemsByItemIdParentUuid = indexBy(
      this.existingLineItems,
      getItemIdParentUuidKey
    )
    // Values are arrays of children
    this.currentLineItemChildrenByParentUuid = groupBy(
      this.existingLineItems,
      'parentUuid'
    )

    console.debug(
      'Created cart with',
      this.existingLineItems.length,
      'line items',
      lineItems
    )
    this.fixInitialState()
  }

  private destroyOrphans() {
    this.allLineItems.forEach((lineItem) => {
      if (!lineItem.parentUuid) {
        return
      }

      if (!this.currentLineItemsByUuid[lineItem.parentUuid]) {
        console.debug('Orphan with missing parent detected', { ...lineItem })
        this.orphans.push(lineItem)
        // Delete proofs assuming children can always be recreated and to clean up invalid state
        this.destroyLineItem(lineItem, 'Delete')
      }
    })
  }

  private destroyDuplicates() {
    this.allLineItems
      // Sort to find duplicates easily
      // http://stackoverflow.com/questions/840781/easiest-way-to-find-duplicate-values-in-a-javascript-array/840808
      .sort((a, b) => a.uuid.localeCompare(b.uuid))
      .filter((item, index, items) => item.uuid === items[index + 1]?.uuid)
      .forEach((lineItem) => {
        this.duplicates.push(lineItem)
        // Don't destroy the proof so it's possible for CS to recover it if necessary
        this.destroyLineItem(lineItem)
      })
  }

  /**
   * Fixes integrity issues with the initial state.
   * - Orphans will be moved to the same location (cart vs saved for later) as their top-most parent.
   * - Line items with duplicate UUIDs will be destroyed (with 1 remaining).
   * - Quantity sync inconsistencies updated.
   * - Saved for later status is synced down from the root.
   */
  private fixInitialState() {
    this.destroyOrphans()
    this.destroyDuplicates()
    this.syncQuantitiesAndSavedForLater()
  }

  /**
   * Sync quantities and saved for later recursively
   */
  private syncQuantitiesAndSavedForLater() {
    this.rootLineItems.forEach(this.syncToChildren.bind(this))
  }

  /**
   * Update the mutable cart with an array of line item operations.
   *
   * CAUTION: Adding operations incrementally vs passing them all at once in `create`, can reduce performance
   * due to additional network roundtrips for fetching item data. Prefer to instantiate the cart with all operations
   * at once in `create` instead. This method may be deprecated in the future to enforce this best practice.
   *
   * @param lineItemOps An array of line item operations to be applied in order.
   * @returns The updated line items resulting from all the operations and subsequent state syncing.
   */
  async update(lineItemOps: LineItemOpRequest[]) {
    console.group(
      'Updating cart with',
      lineItemOps.length,
      'line item operations',
      lineItemOps
    )

    // Fail fast to catch likely bugs and prevent unexpected scenarios
    lineItemOps.forEach(validateOp)

    this.processOps(await withItemData(lineItemOps))
    // Sync state for consistency after all ops have been processed.
    // There may be a few edge cases that deferred syncing misses (vs syncing after each op),
    // but such scenarios should never happen and would imply a logical error by the caller.
    this.syncQuantitiesAndSavedForLater()

    console.debug('Updated cart', this.allLineItems)
    console.groupEnd()

    // TODO: Consider returning void, because this exposes an internal implementation detail
    return this.allLineItems
  }

  /**
   * Processes an array of line item operations and returns an array of resulting line items.
   * The resulting line items may be existing line items or new ones and will not contain any children.
   */
  private processOps(lineItemOps: LineItemOpWithItem[]) {
    return lineItemOps.map((op) => {
      // Process the parent first, which ensures we have a parent line item with a UUID
      /**
       * Line item identified by the op.
       * Can be an existing (found) line item, a new one,
       * or `null` - indicating that a line item was destroyed (or attempted to be destroyed),
       * and that children don't need to be checked.
       */
      const parent = this.processOp(op)
      if (parent) {
        const childrenOps = op.children || []
        // Update children ops to reference the parent by its current UUID, even if the UUID was updated.
        // This mutates the child op only.
        // The actual line item will be updated via processOp.
        childrenOps.forEach((childOp) => (childOp.parentUuid = parent.uuid))
        const childrenLineItemsOperatedOn = this.processOps(
          childrenOps
        ).flatMap((item) => (item ? [item] : []))

        if (op._destroyExtraChildren) {
          console.group('Destroying extra children')
          const uuidsOfChildrenLineItemsOperatedOn =
            childrenLineItemsOperatedOn.reduce(
              (uuids, child) => uuids.add(child.uuid),
              new Set()
            )
          this.findChildren(parent).forEach((child) => {
            if (!uuidsOfChildrenLineItemsOperatedOn.has(child.uuid)) {
              // Destroy any existing direct child that wasn't referenced in the current line item operation
              console.debug('Destroy extra child', child)
              // Extra children destroyed are assumed to be add-ons.
              // These should be deleted in proof service instead of the default `Cancel` (soft-delete),
              // because there's no purpose in keeping the related records,
              // which are only used for parent/child tracking.
              // This frees up storage and reduces noise in proof service.
              // TODO: Investigate whether this is always unnecessary and redundant.
              //   If the "/eco/personalize/update" API already handles this, use `null` instead.
              this.destroyLineItem(child, 'Delete')
            }
          })
          console.groupEnd()
        }
      }
      return parent
    })
  }

  /**
   * Track line items found by ops to aid with migration to mutable cart.
   * While it's technically allowed to locate the same line item across multiple ops,
   * it shouldn't happen if the caller is sending the minimal and logical data.
   * This check is extra strict to help catch programming mistakes in the short term,
   * and can be removed once all cart updates are stable and optimal.
   */
  private validateUnique(existingLineItem: MutableCartLineItem) {
    if (this.processedExistingLineItemUuids.has(existingLineItem.uuid)) {
      // Line items are identified after getting found by any criteria, so this will throw an error
      // even if the same line item was located first by UUID, and then by item ID + parent UUID.
      // The other advantage of tracking ops vs applied line item updates, is that tracking ops
      // only, allows multiple updates of a line item due to implicit behavior like quantity sync.
      throw new Error(
        'Existing line item found by more than one op: ' + existingLineItem.uuid
      )
    } else {
      this.processedExistingLineItemUuids.add(existingLineItem.uuid)
    }
  }

  /**
   * Process an update and return the line item it applies to.
   * This will be the mutated existing line item if it exists,
   * or a new line item if an existing line item was not found.
   */
  private processOp(lineItemOp: LineItemOpWithItem) {
    console.debug('Process op', Object.assign({}, lineItemOp))
    const existingLineItem = this.findLineItem(lineItemOp)

    // Destroy takes precedence over any other operation
    if (lineItemOp._destroy) {
      // `_destroy` is idempotent;
      // if _destroy can't find an existing line item, the result is that it doesn't exist,
      // just like if it actually did find a line item.
      if (existingLineItem) {
        this.destroyLineItem(existingLineItem, lineItemOp._destroyProofAction)
      }

      // Signals to the caller that the line item doesn't exist because it was destroyed, or didn't exist.
      // Therefore, children don't need to be processed because none exist.
      return null
    } else if (existingLineItem) {
      console.debug('Found existing line item', { ...existingLineItem })
      this.validateUnique(existingLineItem)

      this.updateLineItem(existingLineItem, lineItemOp)
      return existingLineItem
    }

    const createdLineItem = this.createLineItem(lineItemOp)
    this.processedExistingLineItemUuids.add(createdLineItem.uuid)
    return createdLineItem
  }
  private get allLineItems() {
    return this.existingLineItems.concat(this.newLineItems)
  }

  private get rootLineItems() {
    return this.allLineItems.filter((lineItem) => !lineItem.parentUuid)
  }

  /**
   * Find an existing line item from a line item update.
   *
   * This may match a line item that exists from the current cart or saved for later,
   * or a line item from the current updates.
   *
   * Mercury also accepts a line item ID, which would be the highest priority,
   * but this limited cart implementation doesn't support that because it only targets Avetti.
   *
   * Order of precedence:
   *   1. UUID
   *   2. Combination of item ID and parent ID
   */
  private findLineItem(
    lineItemOp: LineItemOpRequest
  ): MutableCartLineItem | null {
    if (lineItemOp.lineItemId) {
      // lineItemId is only used with Mercury to update uuid.
      // Fail fast if it's used now because it won't do anything.
      // TODO: Remove if this implementation is still used after Avetti is removed
      throw new Error('Operations with a lineItemId are not yet supported')
    }
    if (!lineItemOp.uuid && !lineItemOp.itemId) {
      throw new Error(
        'Line item operation requires at least one identifier (uuid or itemId)'
      )
    }

    // Find by UUID first
    if (lineItemOp.uuid) {
      const lineItem = this.findLineItemByUuid(lineItemOp.uuid)
      if (lineItem) {
        return lineItem
      } else {
        // If there's a UUID in the op, but no match, don't find by item ID so a new item is created
        return null
      }
    }
    // Lastly, find by item ID and parent ID combination
    if (lineItemOp.itemId) {
      const lineItem = this.findLineItemByItemIdParentUuid(
        lineItemOp as {
          itemId: number
          parentUuid?: string | null
        }
      )
      if (lineItem) {
        return lineItem
      }
    }
    return null
  }

  private findLineItemByUuid(lineItemUuid: string) {
    return this.currentLineItemsByUuid[lineItemUuid] ?? null
  }

  private findLineItemByItemIdParentUuid(lineItemOrOp: {
    itemId: number
    parentUuid?: string | null
  }) {
    // With this implementation, we wouldn't know if we had duplicates by item ID and parent UUID, but that's OK.
    return (
      this.currentLineItemsByItemIdParentUuid[
        getItemIdParentUuidKey(lineItemOrOp)
      ] ?? null
    )
  }

  private findChildren(lineItem: MutableCartLineItem) {
    return this.currentLineItemChildrenByParentUuid[lineItem.uuid] ?? []
  }

  /**
   * Creates a brand new line item.
   * Ops behave as upserts, so this implies that no existing line item was located.
   */
  private createLineItem(lineItemOp: LineItemOpWithItem) {
    if (!lineItemOp.itemId) {
      throw new Error('Cannot create a line item without a itemId')
    }
    const item = lineItemOp.item
    if (!item) {
      throw new Error(
        `Cannot create a line item without item data for itemId=#{lineItemOp.itemId}`
      )
    }
    if (typeof lineItemOp.quantity === 'number' && lineItemOp.quantity < 0) {
      throw new Error('Cannot create a line item with a negative quantity')
    }
    if (lineItemOp._destroy) {
      throw new Error('Cannot destroy a new line item that does not exist')
    }
    if (lineItemOp.customizationId && !lineItemOp.uuid) {
      // While it's technically possible to omit a `uuid` and have a `customizationId` from a cart perspective,
      // all current app logic requires that the caller pre-assign a `uuid`,
      // so this prevents mistakes under current assumptions.
      throw new Error('Customized line items must have a uuid defined')
    }
    if (lineItemOp.parentUuid && lineItemOp.parentUuid === lineItemOp.uuid) {
      // Avoid a circular self-reference.
      // Detecting a larger loop would be ideal too, but it's more important that the backend does it.
      throw new Error('parentUuid must reference a different item')
    }
    // Technically, it's possible to create a new line item without a `uuid`,
    // but always having one makes it easier to track the line item internally.
    const uuid = lineItemOp.uuid || createUrlSafeUuidV4()
    const defaultQuantity =
      item.minQuantity <= 0 ? item.quantityIncrement : item.minQuantity
    const newLineItem: MutableCartLineItem = {
      uuid,
      itemId: lineItemOp.itemId,
      parentUuid: lineItemOp.parentUuid ?? null,
      customizationId: lineItemOp.customizationId ?? null,
      savedForLater: !!lineItemOp.savedForLater,
      quantity: lineItemOp.quantity ?? defaultQuantity,
      // Can only be set in Avetti by setting the address list item, and updating the recipient list.
      // This will be removed once the address book is extracted out of the cart.
      quantityRecipients: 0,
      quantitySyncMultiplier: lineItemOp.quantitySyncMultiplier ?? null,
      _new: true,
      item
    }

    this.newLineItems.push(newLineItem)
    this.currentLineItemsByUuid[uuid] = newLineItem
    this.currentLineItemsByItemIdParentUuid[
      getItemIdParentUuidKey(newLineItem)
    ] = newLineItem
    if (newLineItem.parentUuid) {
      this.currentLineItemChildrenByParentUuid[newLineItem.parentUuid] =
        this.currentLineItemChildrenByParentUuid[newLineItem.parentUuid] || []
      this.currentLineItemChildrenByParentUuid[newLineItem.parentUuid]!.push(
        newLineItem
      )
    }

    return newLineItem
  }

  /**
   * Copy allowed fields to the existing line item and update quantity according to rules.
   */
  private updateLineItem(
    existingLineItem: MutableCartLineItem,
    lineItemOp: LineItemOpRequest
  ) {
    console.debug('Update line item', { ...existingLineItem })

    // This could be done more elegantly, but we're keeping the example simple to make things obvious.
    if (typeof lineItemOp.savedForLater === 'boolean') {
      // May be overwritten later since the root parent's value will be copied recursively
      existingLineItem.savedForLater = lineItemOp.savedForLater
    }
    if (lineItemOp.itemId !== undefined) {
      this.updateItemIdParentUuidState(existingLineItem, lineItemOp.itemId)
    }
    if (
      lineItemOp.parentUuid &&
      lineItemOp.parentUuid !== existingLineItem.parentUuid
    ) {
      // Avetti isn't even aware of parents, so changes cannot be handled here.
      // Mercury will support this in the future, but other systems would need to be synced up too.
      throw new Error(
        'Switching parentUuid is unsupported: ' + existingLineItem.uuid
      )
    }
    if (
      lineItemOp.customizationId &&
      lineItemOp.customizationId !== existingLineItem.customizationId
    ) {
      // Avetti isn't even aware of customizationId, so changes cannot be handled here.
      // Mercury will support this in the future.
      throw new Error(
        'Switching customizationId is unsupported: ' + existingLineItem.uuid
      )
    }
    if (lineItemOp.customizationId !== undefined) {
      // Doesn't do anything for now because Avetti never sees this any only uses uuid as proofId
      existingLineItem.customizationId = lineItemOp.customizationId
    }
    // May be overwritten later by quantity sync
    this.updateQuantity(
      existingLineItem,
      lineItemOp.quantity,
      lineItemOp._incrementQuantity
    )

    console.debug('Line item updated', existingLineItem)
  }

  private destroyProofAction(
    destroyedLineItem: MutableCartLineItem,
    explicitDestroyProofAction: LineItemOpDestroyProofAction | undefined
  ): LineItemOpDestroyProofAction {
    if (explicitDestroyProofAction !== undefined) {
      // An explicit action on the destroyed line item op (or passed from a parent) takes precedence
      return explicitDestroyProofAction
    } else if (destroyedLineItem.customizationId) {
      // Otherwise, if the line item is customized, use `Cancel`
      return 'Cancel'
    } else {
      // Do nothing for everything else (i.e. non-personalized items)
      return null
    }
  }

  private destroyLineItem(
    lineItem: MutableCartLineItem,
    destroyProofAction?: LineItemOpDestroyProofAction
  ) {
    console.debug('Destroy line item', { ...lineItem })
    if (lineItem._new) {
      // It's possible that a new line item could be deleted in subsequent operations.
      // We could just delete the new line item from the mutable cart as if the new line item
      // never existed. However, it doesn't make sense for a client to request a series of operations
      // that would negate themselves, so it's better to fail fast on an undesirable request.
      throw new Error(
        'New line item was destroyed by a subsequent operation: ' + lineItem
      )
    }

    // Queue a deletion of the existing line item
    this.destroyedLineItemUuidToProofDestroyAction[lineItem.uuid] =
      this.destroyProofAction(lineItem, destroyProofAction)
    delete this.currentLineItemsByUuid[lineItem.uuid]
    if (lineItem.parentUuid) {
      this.currentLineItemChildrenByParentUuid[lineItem.parentUuid] =
        this.currentLineItemChildrenByParentUuid[lineItem.parentUuid]!.filter(
          (existing) => existing !== lineItem
        )
    }

    const itemIdParentUuidKey = getItemIdParentUuidKey(lineItem)
    // Protect against removing a reference to another item if there are duplicate itemId, parentUuid pairs
    if (
      this.currentLineItemsByItemIdParentUuid[itemIdParentUuidKey] === lineItem
    ) {
      delete this.currentLineItemsByItemIdParentUuid[itemIdParentUuidKey]
    }

    this.existingLineItems = this.existingLineItems.filter(
      (existing) => existing !== lineItem
    )

    // Recursively destroy children
    this.findChildren(lineItem).forEach((child) =>
      this.destroyLineItem(child, destroyProofAction)
    )
  }

  private updateItemIdParentUuidState(
    existingLineItem: MutableCartLineItem,
    updatedItemId: number
  ) {
    if (existingLineItem.itemId != updatedItemId) {
      // Catch an illegal Avetti operation. This will be allowed in the future with Mercury.
      throw new Error(
        'Changing an existing item ID is unsupported. Destroy and commit, then re-add.'
      )
    }

    const oldItemIdKey = getItemIdParentUuidKey(existingLineItem)
    existingLineItem.itemId = updatedItemId

    // It's possible that another existing line item could have matched the key, but matching by
    // itemId is not allowed if the match isn't unique, so we don't attempt to find additional
    // existing matches under the old key when removing this reference.
    delete this.currentLineItemsByItemIdParentUuid[oldItemIdKey]
    this.currentLineItemsByItemIdParentUuid[
      getItemIdParentUuidKey(existingLineItem)
    ] = existingLineItem
  }

  private updateQuantity(
    existingLineItem: MutableCartLineItem,
    updatedQuantity?: number,
    incrementQuantity: boolean = false
  ) {
    if (incrementQuantity) {
      existingLineItem.quantity += updatedQuantity ?? 1

      // Updates that decrement below min quantity imply destroy.
      // Quantity is allowed to be 0 on some items to act as placeholders, but negative quantity is never possible.
      if (existingLineItem.quantity < existingLineItem.item.minQuantity) {
        this.destroyLineItem(existingLineItem)
      }
    } else {
      // Set quantity absolutely

      if (updatedQuantity === undefined) {
        // Silently ignore non-incremental updates without a quantity set
        return
      }

      if (updatedQuantity < 0) {
        throw new Error('Updated quantity is less than 0: ' + updatedQuantity)
      }
      existingLineItem.quantity = updatedQuantity
    }
  }

  /**
   * Recursively syncs state from a parent to children to ensure the cart is in a consistent state.
   * Saved for later must be consistent for all descendants.
   * Quantity sync applies from a parent to its direct children.
   */
  private syncToChildren(parentLineItem: MutableCartLineItem) {
    const children = this.findChildren(parentLineItem)
    syncSavedForLater(parentLineItem, children)
    syncQuantities(parentLineItem, children)
    children.forEach(this.syncToChildren.bind(this))
  }

  /**
   * Returns line items that have changed.
   */
  private get updatedLineItems() {
    return this.existingLineItems.filter((lineItem) => {
      const original = this.originalLineItemsByUuid[lineItem.uuid]!
      return (
        lineItem.quantity !== original.quantity ||
        lineItem.itemId !== original.itemId ||
        lineItem.savedForLater !== original.savedForLater ||
        // Immutable now, but if the implementation is updated and used with Mercury, this could change
        lineItem.parentUuid !== original.parentUuid ||
        lineItem.customizationId !== original.customizationId
      )
    })
  }

  /**
   * Get ops for destroyed, created, or updated line items to commit to the cart.
   */
  commitOps() {
    // Avoid mistakes by ensuring this only happens once, even though the caller is responsible for the commit.
    if (this.committed) {
      throw new Error('MutableCart can only be committed once')
    }
    this.committed = true

    const lineItemOps: LineItemOpRequest[] = []
    console.group('Generating commit ops')
    if (
      Object.keys(this.destroyedLineItemUuidToProofDestroyAction).length > 0
    ) {
      console.debug(
        'Delete line items',
        this.destroyedLineItemUuidToProofDestroyAction
      )
      lineItemOps.push(
        ...Object.entries(this.destroyedLineItemUuidToProofDestroyAction).map(
          destroyLineItemOp
        )
      )
    } else {
      console.debug('Nothing to delete')
    }
    const updatedLineItems = this.updatedLineItems
    if (updatedLineItems.length) {
      console.debug('Update line items', updatedLineItems)
      lineItemOps.push(...updatedLineItems.map(cartItemToUpsertLineItemOp))
    } else {
      console.debug('Nothing to update')
    }
    if (this.newLineItems.length) {
      console.debug('Insert line items', this.newLineItems)
      lineItemOps.push(...this.newLineItems.map(cartItemToUpsertLineItemOp))
    } else {
      console.debug('Nothing to insert')
    }
    console.debug('All line item ops to commit', lineItemOps)
    console.groupEnd()

    return lineItemOps
  }
}
